import autobind from 'core-decorators/lib/autobind';
import classnames from 'classnames';

import { WixConnect } from 'shared/utils/wix-connect';
import getDisplayName from 'shared/utils/get-component-display-name';

import { sides } from 'shared/constants/popout';
import { sideShape } from 'shared/shapes/popout';

import pubsub from 'shared/utils/pubsub';
import events from 'shared/constants/events';

import { showTooltip, hideTooltip } from 'shared/components/tooltip/tooltip-popout';

import styles from './with-tooltip.styl';


export default function withTooltip(WrappedComponent) {

  @WixConnect(styles)
  class ComponentWithTooltip extends React.Component {

    static displayName = `withTooltip(${getDisplayName(WrappedComponent)})`;

    static propTypes = {
      name: React.PropTypes.string,
      popoutSide: sideShape,
      preventHideOnHover: React.PropTypes.bool,
      innerClassName: React.PropTypes.string,
      shownStateClassName: React.PropTypes.string,
      text: React.PropTypes.any,
      noTooltip: React.PropTypes.bool,
      children: React.PropTypes.any,
      getRef: React.PropTypes.func,
      onMouseEnter: React.PropTypes.func,
      onMouseLeave: React.PropTypes.func,
    };

    static defaultProps = {
      popoutSide: sides.TOP,
      preventHideOnHover: false,
      shownStateClassName: '',
      noTooltip: false,
      getRef: _.noop,
      onMouseEnter: _.noop,
      onMouseLeave: _.noop,
    };

    state = {
      tooltipIsShown: false,
    };

    componentDidMount() {
      this._tooltipId = _.uniqueId('with-tooltip-');
      this._tooltipDidShowUnsub = pubsub.subscribe(events.TOOLTIP.DID_SHOW, this.didShowTooltip);
      this._tooltipDidHideUnsub = pubsub.subscribe(events.TOOLTIP.DID_HIDE, this.didHideTooltip);
    }

    componentWillUpdate(nextProps) {
      const { noTooltip } = nextProps;
      if (noTooltip !== this.props.noTooltip) {
        if (noTooltip) {
          this.hideTooltipPopout();
        } else {
          this.showTooltipPopout();
        }
      }
    }

    componentWillUnmount() {
      this._tooltipDidShowUnsub();
      this._tooltipDidHideUnsub();
      hideTooltip();
    }

    @autobind
    didShowTooltip(tooltipId) {
      if (tooltipId === this._tooltipId) {
        this.setState({ tooltipIsShown: true });
      }
    }

    @autobind
    didHideTooltip(tooltipId) {
      if (tooltipId === this._tooltipId) {
        this.setState({ tooltipIsShown: false });
      }
    }

    @autobind
    showTooltipPopout(event) {
      const { noTooltip, popoutSide, preventHideOnHover, innerClassName, text, children, onMouseEnter } = this.props;
      onMouseEnter(event);

      if (noTooltip) {
        return;
      }

      showTooltip({
        tooltipId: this._tooltipId,
        popoutSide,
        preventHideOnHover,
        innerClassName: classnames(styles['tooltip-inner'], innerClassName),
        content: text || children,
        referenceElement: this.wrappedComponent,
      });
    }

    @autobind
    hideTooltipPopout(event) {
      this.props.onMouseLeave(event);

      hideTooltip();
    }

    @autobind
    getWrapperComponentRef(node) {
      this.props.getRef(node);
      this.wrappedComponent = node;
    }

    render() {
      const props = _.omit(this.props, [
        'styles', 'text', 'noTooltip', 'popoutSide', 'innerClassName', 'preventHideOnHover',
      ]);

      const shownStateClassName = classnames({ [this.props.shownStateClassName]: this.state.tooltipIsShown });

      return (
        <WrappedComponent {...props}
                          shownStateClassName={shownStateClassName}
                          getRef={this.getWrapperComponentRef}
                          onMouseEnter={this.showTooltipPopout}
                          onMouseLeave={this.hideTooltipPopout} />
      );
    }
  }

  return ComponentWithTooltip;
}


const adaptProps = convertFunction => Component => props => {
  const adaptedProps = _.isFunction(convertFunction) ?
    convertFunction(props) :
    null;

  return (
    <Component {...adaptedProps} />
  );
};


const joinShownStateWithClassName = props => {
  const { shownStateClassName, className } = props;
  return {
    ..._.omit(props, 'shownStateClassName'),
    className: classnames(className, shownStateClassName),
  };
};


export const adaptShowStatePropsToClassName = adaptProps(joinShownStateWithClassName);



// WEBPACK FOOTER //
// ./src/shared/components/with-tooltip/with-tooltip.js